import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { components } from '@bookingdei/api-client/types';
import { useLanguage } from '../../shell/language-context';
import { HomeListingCard } from './home-listing-card';
import type { HomeFormat } from './use-home-format';

type BusinessSummary = components['schemas']['BusinessSummary'];

// One business carousel row (design DC:681-738 — Recommended, New, Things to do this
// weekend): title, optional sub-line and "See all", a scroll-snapped track of cards
// with a save heart, skeleton cards while the rail loads, desktop arrows (the back
// arrow appears once scrolled), and the optional organic-ranking footnote.
export function BusinessCarousel({
  title,
  sub,
  seeAllTo,
  footnote,
  items,
  fmt,
}: {
  title: string;
  sub?: string;
  seeAllTo?: string;
  footnote?: string;
  // undefined while loading → skeletons.
  items: BusinessSummary[] | undefined;
  fmt: HomeFormat;
}) {
  const { t } = useLanguage();
  const trackRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  if (items && items.length === 0) return null;
  const scroll = (dir: number) => {
    const el = trackRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: 'smooth' });
  };

  return (
    <section className="hm-sec hm-sec--car">
      <div className="hm-head hm-head--car">
        <div>
          <h2 className="hm-h2 hm-h2--car">{title}</h2>
          {sub ? <div className="hm-sub">{sub}</div> : null}
        </div>
        {seeAllTo ? (
          <Link className="hm-link hm-link--nowrap" to={seeAllTo}>
            {t('dc50.car.see_all')}
          </Link>
        ) : null}
      </div>
      <div className="hm-car">
        <div
          className="hm-car__track"
          ref={trackRef}
          aria-busy={!items}
          onScroll={(e) => {
            const s = e.currentTarget.scrollLeft > 10;
            if (s !== scrolled) setScrolled(s);
          }}
        >
          {items
            ? items.map((b) => <HomeListingCard key={b.id} business={b} fmt={fmt} photo="img" heart snap />)
            : [1, 2, 3, 4, 5].map((k) => (
                <div key={k} className="hm-skel" aria-hidden="true">
                  <div className="hm-skel__img" />
                  <div className="hm-skel__line" />
                  <div className="hm-skel__line hm-skel__line--short" />
                </div>
              ))}
        </div>
        {scrolled ? (
          <button type="button" className="hm-car__arrow hm-car__arrow--prev" aria-label={t('dc50.car.prev')} onClick={() => scroll(-1)}>
            ‹
          </button>
        ) : null}
        <button type="button" className="hm-car__arrow hm-car__arrow--next" aria-label={t('dc50.car.next')} onClick={() => scroll(1)}>
          ›
        </button>
      </div>
      {footnote ? <div className="hm-note hm-note--car">{footnote}</div> : null}
    </section>
  );
}
